import React, { useState } from "react";
import {
  FlexGrid,
  Heading,
  Text,
  CTA,
  formatCurrency,
  Input,
} from "@clearabee/ui-library-native";
import { Button as SubmitButton } from "components/Core/Button/Button";
import { Platform, View } from "react-native";
import { Image } from "components/Image/Image";
import { styles, ids } from "./SkipBags.styles";
import placeholderImage from "lib/images/placeholder.png";
import { useTranslation } from "react-i18next";
import { useCatalogue } from "@clearabee/ui-sdk";
import { sortByPrice } from "lib/utils";
import { useQuestionContext } from "lib/providers/questionProvider";
import { FormState } from "./SkipBags.validation";

export const SelectBagBagAndCollection = (): React.ReactElement => {
  const [translate] = useTranslation();
  const { defaultCatalogue } = useCatalogue();
  const context = useQuestionContext<FormState>();

  const filteredItems = sortByPrice(
    defaultCatalogue.items.filter(
      (item) => item.parentSku === "BAGCOLLECTIONSKIPBAGS",
    ),
  );

  const [items, setItems] = useState<Record<string, number>>({});
  const disabled = !Object.values(items).reduce((a, b) => a + b, 0);

  return (
    <View>
      <FlexGrid
        cellSpacing="xsmall"
        rowSpacing="xsmall"
        wrap="wrap"
        style={styles.gridMarginTop}
      >
        {filteredItems
          .filter((item) => item.price)
          .map(({ sku, title, img, price, description }) => (
            <FlexGrid.Cell
              key={title}
              base="50%"
              medium="33.33%"
              large="33.33%"
            >
              <CTA
                background="light"
                orientation="portrait"
                shadow={Platform.OS === "web"}
                styles={styles.CTA}
                dataSet={{ media: ids.CTA }}
                contentStyles={{ flex: 1 }}
                heading={
                  <Heading
                    level={4}
                    styles={styles.CTAHeading}
                    dataSet={{ media: ids.CTAHeading }}
                  >
                    {title}
                  </Heading>
                }
                textColor="brand"
                pill={translate(
                  "hwa.components.skipBags.types.BagAndCollection",
                )}
                image={
                  <Image
                    source={img ?? placeholderImage}
                    resizeMode="contain"
                    style={styles.CTAImage}
                  />
                }
                subheading={
                  <View
                    style={{
                      flex: 1,
                    }}
                  >
                    <Text
                      styles={styles.description}
                      dataSet={{ media: ids.description }}
                    >
                      {description}
                    </Text>
                    <Text
                      color="negative.base"
                      styles={styles.descriptionDisclaimer}
                      dataSet={{ media: ids.descriptionDisclaimer }}
                    >
                      {translate(
                        "hwa.screens.selectProduct.notSuitableSkipbagContents",
                      )}
                    </Text>
                    <View style={styles.subheadingContainer}>
                      <Text
                        styles={styles.subheading}
                        dataSet={{ media: ids.subheading }}
                        numberOfLines={1}
                      >
                        {formatCurrency(price ?? 0)}
                        <Text> {translate("common.incVat")}</Text>
                      </Text>
                    </View>
                  </View>
                }
              >
                <View style={styles.quantityWrap}>
                  <Input.Quantity
                    name={sku}
                    min={0}
                    max={99}
                    value={items[sku] ?? 0}
                    onChange={(value) => setItems({ ...items, [sku]: value })}
                  />
                </View>
              </CTA>
            </FlexGrid.Cell>
          ))}
      </FlexGrid>
      <SubmitButton
        type="button"
        onClick={() => {
          context.set("products", items, "open").submit();
        }}
        disabled={disabled}
        size="medium"
        color="warning"
        styles={{ alignSelf: "center" }}
      >
        {translate("common.continue")}
      </SubmitButton>
    </View>
  );
};
